import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import Logo from './Logo';
import { useLocale } from '../context/LocaleContext';
import { HOME_CONTACT_SECTION_ID, HOME_NAV_SECTIONS, homeSectionHref } from '../config/homeNav';
import { scrollToSection } from '../utils/scrollToSection';

const NavCta = ({ className = '', onClick, label }) => (
  <a
    href={homeSectionHref(HOME_CONTACT_SECTION_ID)}
    className={`rihen-btn rihen-btn-primary rihen-nav-cta ${className}`.trim()}
    onClick={onClick}
  >
    <span className="rihen-nav-cta__fire" aria-hidden="true">
      <span className="rihen-nav-cta__tongue rihen-nav-cta__tongue--1" />
      <span className="rihen-nav-cta__tongue rihen-nav-cta__tongue--2" />
      <span className="rihen-nav-cta__tongue rihen-nav-cta__tongue--3" />
    </span>
    <span className="rihen-nav-cta__label">
      <Megaphone size={16} className="rihen-nav-cta__icon" aria-hidden="true" />
      {label}
    </span>
  </a>
);

const NAVBAR_HEIGHT = 72;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLocale();

  const isHome = location.pathname === '/';
  const navSolid = !isHome || pastHero;
  const navCompact = isHome && scrolled && !pastHero;

  useEffect(() => {
    let frame = 0;

    const updateNavState = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 12);

      if (!isHome) {
        setPastHero(true);
        return;
      }

      const hero = document.getElementById('hero');
      if (!hero) {
        setPastHero(scrollY > 120);
        return;
      }

      const heroBottom = hero.getBoundingClientRect().bottom;
      setPastHero(heroBottom <= NAVBAR_HEIGHT);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateNavState();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateNavState();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isHome, location.pathname]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const isActive = (sectionId) => isHome && location.hash === `#${sectionId}`;

  const handleSectionClick = (sectionId) => (event) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const handleContactClick = (event) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection(HOME_CONTACT_SECTION_ID);
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`rihen-nav${navSolid ? ' solid' : ''}${navCompact ? ' compact' : ''}${isHome ? ' on-light-hero' : ''}`}
        id="nav"
      >
        <div className="rihen-nav-inner">
          <Link
            to="/"
            className="rihen-brand"
            aria-label="TFFM Portfolio"
            onClick={(event) => {
              if (!isHome) return;
              event.preventDefault();
              window.history.replaceState(null, '', '/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setIsMenuOpen(false);
            }}
          >
            <Logo imgClassName="rihen-brand-logo" />
          </Link>

          <div className="rihen-nav-links">
            {HOME_NAV_SECTIONS.map((item) => (
              <a
                key={item.id}
                href={homeSectionHref(item.id)}
                className={`rihen-nav-link ${isActive(item.id) ? 'active' : ''}`}
                aria-current={isActive(item.id) ? 'location' : undefined}
                onClick={handleSectionClick(item.id)}
              >
                <span className="rihen-nav-link__text">{t(item.labelKey)}</span>
                <span className="rihen-nav-link__rule" aria-hidden="true">
                  <span className="rihen-nav-link__open">&lt;</span>
                  <span className="rihen-nav-link__track">
                    <span className="rihen-nav-link__bar" />
                  </span>
                  <span className="rihen-nav-link__close">&gt;</span>
                </span>
              </a>
            ))}
          </div>

          <div className="rihen-nav-right">
            <button
              type="button"
              className="rihen-lang-toggle"
              onClick={toggleLang}
              aria-label={t('lang_toggle_aria')}
            >
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <NavCta className="rihen-nav-cta-desktop" label={t('nav_cta')} onClick={handleContactClick} />
            <button
              type="button"
              className="rihen-burger"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label={isMenuOpen ? t('menu_close') : t('menu_open')}
              aria-expanded={isMenuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      <div className={`rihen-mobile-menu ${isMenuOpen ? 'open' : ''} ${isHome ? 'on-light-hero' : ''}`}>
        {HOME_NAV_SECTIONS.map((item) => (
          <a
            key={item.id}
            href={homeSectionHref(item.id)}
            className={`rihen-mobile-link ${isActive(item.id) ? 'active' : ''}`}
            onClick={handleSectionClick(item.id)}
          >
            <span className="rihen-nav-link__open">&lt;</span>
            {t(item.labelKey)}
            <span className="rihen-nav-link__close">&gt;</span>
          </a>
        ))}
        <NavCta onClick={handleContactClick} label={t('nav_cta')} />
      </div>
    </>
  );
};

export default Navbar;
